import React from 'react';
import { DivIcon } from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import moment from 'moment';
import { dateFormats } from 'utils/dateFormats';

import { Rank, rankColors } from 'views/components/DrivingControl/RankIcon';

import { setRouteTypeColors } from 'consts/vehicles';
import { setIncidentsTypes } from 'consts/adas';

import { RType } from 'types/vehicles';
import { IHistoryIncident } from 'views/pages/MapPage';
import { convertHexToRGBA } from 'utils/color';

const ClusterIcon = styled.div<{ size: number; backgroundColor: string }>`
  height: 50px;
  width: 50px;
  //box-shadow: 0px 0px 0px 11px rgba(34, 60, 80, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: ${({ backgroundColor }) => backgroundColor};
  background-clip: padding-box;

  transform: translateX(-50%) translateY(-50%);
  left: 50%;
  top: 50%;
  position: absolute;

  > div {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 25px;
    height: 25px;
    border-radius: 50%;

    background: #ffffff;

    > span {
      font-size: ${({ size }) => (size <= 99 ? '17px' : '14px')};
    }
  }
`;

const ClusterIconPolygon = styled.div<{ size: number; backgroundColor: string }>`
height: 50px;
width: 50px;
display: flex;
justify-content: center;
align-items: center;
background: ${({ backgroundColor }) => backgroundColor};
background-clip: padding-box;

clip-path: polygon(
  50% 0%, 
  100% 25%, 
  100% 75%, 
  50% 100%, 
  0% 75%, 
  0% 25%
);

transform: translateX(-50%) translateY(-50%);
left: 50%;
top: 50%;
position: absolute;

> div {
  display: flex;
  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  clip-path: polygon(
    50% 0%, 
    100% 25%, 
    100% 75%, 
    50% 100%, 
    0% 75%, 
    0% 25%
  );
  background: #ffffff;

  > span {
    font-size: ${({ size }) => (size <= 99 ? '17px' : '14px')};
  }
}
`;


const IncidentPoint = styled.div<{color: string}>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  background: ${({ color }) => `${color}`};
  opacity: 1;
  
  ::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    width: 4px;
    height: 4px;
    background-color: #FFF;
    transform: translate(-50%, -50%);
  }
`

const IncidentClusterIcon = styled.div<{ color: string, count: number, zoom: number }>`
  width: ${({count, zoom})=> `${(70 + (0.1*Math.cbrt(count))*zoom)}px`};
  height: ${({count, zoom})=> `${(70 + (0.1*Math.cbrt(count))*zoom)}px`};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: ${({ color }) => `${color}`};
  transform: translateX(-50%) translateY(-50%);
  left: 50%;
  top: 50%;
  position: absolute;
  filter: blur(10px);
  // opacity: 0.7;
`;

export const stopIcon = (selected?: boolean | string, stroke?: string) => {
  let fill = '#003a72';
  if (typeof selected === 'string') {
    fill = selected;
  } else if (selected === true) fill = '#d80011';

  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        height="28"
        width="28"
        viewBox="0 0 40 40"
        style={{
          transform: 'translateX(-50%) translateY(-50%)',
          left: '50%',
          top: '50%',
          position: 'absolute',
        }}
      >
        <circle
          fill={fill}
          stroke={stroke || '#FFF'}
          strokeWidth="3"
          r="14"
          cx="15"
          cy="15"
        />
      </svg>
    ),
  });
};

export const correspondenceIcon = (cnt: number, strokeColor?: string) => {
  let size = cnt.toString().length;
  if (size < 2) size = 2;

  const svgSize = size * 10 + 3;

  const color = strokeColor || '#d80011';

  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        height={svgSize}
        width={svgSize}
        viewBox="40 40"
        style={{
          transform: 'translateX(-50%) translateY(-50%)',
          left: '50%',
          top: '50%',
          position: 'absolute',
        }}
      >
        <circle
          fill={'#ffffff'}
          stroke={color}
          strokeWidth="3"
          r={size * 5}
          cx={svgSize / 2}
          cy={svgSize / 2}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          stroke={'#000'}
          strokeWidth="1px"
          dy=".3em"
        >
          {cnt}
        </text>
      </svg>
    ),
  });
};

export const selectableStop = ({
  selected,
  cnt,
  color: strokeColor,
  size,
}: {
  selected: boolean;
  cnt: number;
  color?: string;
  size: number;
}) => {
  const svgSize = size * 2 + 3;

  const color = strokeColor || '#d80011';

  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        height={svgSize}
        width={svgSize}
        viewBox="40 40"
        style={{
          transform: 'translateX(-50%) translateY(-50%)',
          left: '50%',
          top: '50%',
          position: 'absolute',
        }}
      >
        <circle
          fill={selected ? '#ffffff' : '#003a72'}
          stroke={selected ? color : '#ffffff'}
          strokeWidth="3"
          r={size}
          cx={svgSize / 2}
          cy={svgSize / 2}
        />
        {selected && (
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            stroke={'#000'}
            strokeWidth="1px"
            dy=".3em"
          >
            {cnt}
          </text>
        )}
      </svg>
    ),
  });
};

const Circle = styled.div<{ bg: string }>`
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  background: ${({ bg }) => bg};
  overflow: hidden;

  span {
    position: absolute;
    transform: translateX(-50%) translateY(-50%);
    top: 50%;
    left: 50%;
    z-index: 1000;
    font-size: 14px;
    color: #333333;
  }
`;

const Part = styled.div<{ bg: string; index: number }>`
  position: absolute;
  top: 0;
  width: 50%;
  height: 50%;
  background: ${({ bg }) => bg};
  span {
    color: ${({ index }) =>
      index === 3 || index === 4 ? '#FFFFFF' : '#333333'} !important;
  }
`;

export const eventClusterIcon = (ranks: { [rank: number]: number }) =>
  new DivIcon({
    html: ReactDOMServer.renderToString(
      <Circle bg={rankColors[Number.parseInt(Object.keys(ranks)[0])]}>
        <Part
          index={parseInt(Object.keys(ranks)[0])}
          bg={rankColors[parseInt(Object.keys(ranks)[0]) as Rank]}
          style={{
            top: Object.keys(ranks).length > 1 ? '50%' : 0,
            left: 0,
            width: Object.keys(ranks).length === 4 ? '50%' : '100%',
            height: Object.keys(ranks).length > 1 ? '50%' : '99%',
          }}
        >
          <span>{ranks[parseInt(Object.keys(ranks)[0])]}</span>
        </Part>
        {Object.keys(ranks)
          .slice(1)
          .map((part, index) => (
            <Part
              index={parseInt(part)}
              bg={rankColors[parseInt(part) as Rank]}
              style={{
                top: index > 1 ? '50%' : 0,
                left:
                  index % 2 === 0 && Object.keys(ranks).length !== 2
                    ? '50%'
                    : 0,
                ...(Object.keys(ranks).length === 2 ? { width: '100%' } : {}),
              }}
            >
              <span>{ranks[parseInt(part)]}</span>
            </Part>
          ))}
      </Circle>
    ),
  });

export const eventIcon = (rank: number) =>
  new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        height="28"
        width="28"
        viewBox="0 0 40 40"
        style={{
          transform: 'translateX(-50%) translateY(-50%)',
          left: '50%',
          top: '50%',
          position: 'absolute',
        }}
      >
        <circle
          fill={rankColors[rank as Rank]}
          stroke="#FFF"
          strokeWidth="3"
          r="14"
          cx="15"
          cy="15"
        />
      </svg>
    ),
  });

export const userIcon = () =>
  new DivIcon({
    html: ReactDOMServer.renderToString(
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        fill="white"
        style={{
          transform: 'translateX(-50%) translateY(-50%)',
          left: '50%',
          top: '50%',
          position: 'absolute',
        }}
      >
        <title>Abstract user icon</title>

        <defs>
          <clipPath id="circular-border">
            <circle cx="20" cy="20" r="10" />
          </clipPath>
          <clipPath id="avoid-antialiasing-bugs">
            <rect width="100%" height="40" />
          </clipPath>
        </defs>

        <circle
          cx="20"
          cy="20"
          r="10"
          fill="black"
          clipPath="url(#avoid-antialiasing-bugs)"
        />
        <circle cx="20" cy="17" r="5" />
        <circle cx="20" cy="28" r="8" clipPath="url(#circular-border)" />
      </svg>
    ),
  });

export const clusterIcon = ({
  routeType,
  size,
  isVehicle,
  incidentType,
  fillColor
}: {
  routeType: RType;
  size: number;
  isVehicle: boolean;
  incidentType?: number;
  fillColor?: string;
}) => {
  const routeTypeColor = fillColor ?? setRouteTypeColors()[routeType];
  const currentIncident = incidentType && setIncidentsTypes()[incidentType];
  const incidentColor = currentIncident && currentIncident.color
    ? convertHexToRGBA(currentIncident.color, 70)
    : 'rgba(205, 205, 205, 0.7)';

const backgroundColor = incidentType ? incidentColor : routeTypeColor;

  if (isVehicle) {
    return new DivIcon({
      html: ReactDOMServer.renderToString(
        <ClusterIcon size={size}
          backgroundColor={backgroundColor}
        >
          <div>
            <span>{size}</span>
          </div>
        </ClusterIcon>
      ),
    })
  }

  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <ClusterIconPolygon size={size}
        backgroundColor={backgroundColor}
      >
        <div>
          <span>{size}</span>
        </div>
      </ClusterIconPolygon>
    ),
  })
};

export const incidentsIcon = ({
  type,
  count,
  zoom
}: {
  type: number;
  count: number;
  zoom: number;
}) => {
  const currentIncident = setIncidentsTypes()[type];
  const color = currentIncident?.color
    ? convertHexToRGBA(currentIncident.color, 70)
    : 'rgba(205, 205, 205, 0.7)';

  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <IncidentClusterIcon color={color} count={count} zoom={zoom} />
    ),
  })
}

export const incidentPointIcon = (point: IHistoryIncident) => {
  const currentIncident = setIncidentsTypes()[point.incident_type];

  const incidentName = currentIncident?.shortName ? currentIncident?.shortName : 
  currentIncident?.name ? currentIncident?.name : `Событие ИСПВ ${point.incident_type}`;

  const color = currentIncident?.color
    ? currentIncident?.color
    : 'rgba(205, 205, 205, 1)';
  
  const titleText = `
  Гаражный номер: ${point.depot_number || ''}
  ${incidentName}
  Время: ${point.tm ? moment
    .utc(point.tm)
    .local()
    .format(dateFormats.readableDateTimeSeconds) : ''}
  Скорость: ${point.speed ? point.speed : 0} км/ч
  Координаты: ${point.lat && point.lon ? `${point.lat}; ${point.lon}` : ''}
  `

  return new DivIcon({
    html: ReactDOMServer.renderToString(
      <IncidentPoint title={titleText} color={color} />
    )
  });
}
